import {
  Currencies,
  CurrenciesSymbols,
} from "../../lib/helpers/getCurrencySymbol";
import { totalBill } from "../../lib/helpers/totalBill";
import {
  ProductStorePriceDescriptor,
  ProductStorePriceItem,
  ProductStorePrices,
  SubscriptionTerm,
} from "./ProductStore.types";

// A supported currency as its own ID segment: "goget-one-v2-SEK",
// "Total-Care-Support-1-Year-USD-Yearly", "...-USD-Every-3-years".
// Chargebee appends the currency after the item ID, so the last such segment
// wins: "Shipping-International-V2-USD-SEK" is the SEK price of an item whose
// ID happens to contain "USD".
const CURRENCY_IN_PRICE_ID = new RegExp(
  `^.*-(${Object.keys(CurrenciesSymbols).join("|")})(?:-|$)`
);

/**
 * The customer's currency, read from the price IDs. IDs without a currency
 * segment (e.g. "goget-one---display") are ignored; the rest must agree.
 */
export const getPriceCurrency = (
  items: ProductStorePriceItem[]
): Currencies | undefined => {
  const currencies = new Set(
    items
      .map((item) => item.priceId.match(CURRENCY_IN_PRICE_ID)?.[1])
      .filter(Boolean)
  );

  return currencies.size === 1
    ? (Array.from(currencies)[0] as Currencies)
    : undefined;
};

/** "$695", "6950kr", "$44.95", in the Billing page's format. */
export const formatPrice = (cents: number, currency: Currencies) =>
  totalBill(
    currency,
    cents % 100 === 0 ? cents / 100 : (cents / 100).toFixed(2)
  );

export interface ResolvedPrice {
  /** The Chargebee price ID, used for checkout. */
  priceId: string;
  /** Display text, e.g. "$695 + shipping". */
  text: string;
}

/**
 * Finds a card's price in the customer's prices. Term-priced add-ons need a
 * known term. Returns undefined when there is no matching price.
 */
export const resolveProductPrice = (
  descriptor: ProductStorePriceDescriptor | undefined,
  prices: Pick<ProductStorePrices, "currency" | "items">,
  term?: SubscriptionTerm
): ResolvedPrice | undefined => {
  const { currency, items } = prices;

  if (!descriptor || !currency) {
    return undefined;
  }

  let variant: { priceId: string; text: string } | undefined = descriptor;

  if (descriptor.threeYear) {
    variant =
      term === "3-year"
        ? descriptor.threeYear
        : term === "1-year"
          ? descriptor
          : undefined;
  }

  if (!variant) {
    return undefined;
  }

  const priceId = variant.priceId.replace("{currency}", currency);
  const item = items.find((price) => price.priceId === priceId);

  return item
    ? {
        priceId,
        text: variant.text.replace(
          "{price}",
          formatPrice(item.priceCents, currency)
        ),
      }
    : undefined;
};

/**
 * A shipping price from its `{currency}` pattern, e.g.
 * "Shipping-International-V3-{currency}", or undefined when the customer has none.
 */
export const resolveShipping = (
  pattern: string,
  { currency, items }: Pick<ProductStorePrices, "currency" | "items">
): ProductStorePriceItem | undefined => {
  if (!currency) {
    return undefined;
  }

  const priceId = pattern.replace("{currency}", currency);

  return items.find((item) => item.priceId === priceId);
};
